import React, { useCallback, useEffect, useMemo, useState } from "react";
import DrillOver from "../shared/DrillOver";
import { PanelEmpty, PanelLoadFailed } from "../shared/PanelShell";
import ConversationRail from "./ConversationRail";
import AnalysisColumn from "./AnalysisColumn";
import ConversationDetailPane, { ConversationHeader } from "./detail/ConversationDetailPane";
import { outcomeAccentClass, outcomeHoverClass } from "./lib/outcomeStyles";
import PersonProfile from "../cdp/PersonProfile";
import { formatTimeOnlySafe, getMsgSide, getMsgText, getMsgTime } from "./lib/messageShape";
import { loadConversationData } from "./conversationData";
import { getConversationList, getConversationIntelMap, drill as fnDrill } from "../../api/conversation";
import { downloadTranscriptsCsv } from "../../api/auth";
import { DEFAULT_INTEL_FILTERS, sortRailRows, intelExtraColumns } from "./list/conversationIntel";
import { fmtCurrency, fmtDateLabel, titleCase, labelFor } from "../shared/format";

// CONVERSATION PAGE — the shell both MY AGENTS pages are (V6 §B.1). One component on
// two routes; `modality` ('voice' | 'chat') is the only difference the page itself
// carries. It owns the load-time fetch (loadConversationData — every modality-scoped
// RPC threaded with the page's modality), the rail's own list fetch, the drill stack,
// and the rail-filter state that a drill sets.
//
// TWO-COLUMN GRID WITH min-height: 0 ON BOTH COLUMNS. Grid children default to
// `min-height: auto`, and the analysis column REFUSES to scroll without the reset —
// the spec calls this out as having cost a day. Both columns get `min-h-0`; the rail
// is a fixed 340px spine that never leaves, and each column scrolls independently.
//
// EVERY CLICK GOES TO ONE PLACE (§B principle 3): a funnel stage, a finding, a
// barrier, a rail row — all open the same stacking DrillOver. A stage does three
// things at once (§F.4): highlights, filters the rail to its conversation_ids, and
// opens the drill; the RailFilterChip's dismiss clears all three.
//
// SCOPE OF THIS SHELL: the load fetch, the two-column layout, the drill stack and the
// drain→rail flow are wired and tested here. The DrillOver's conversation/guest
// bodies (the extended ConversationDetailPane to §E.16, and PersonProfile) are §8;
// this shell opens a set into a clickable list and a conversation into a titled
// placeholder that names where the detail pane mounts. The rail's search/Tags/sort/
// export (§E.15) are follow-up. Routing + useView wiring (client id, date range) is
// §11 — this component takes them as props so it is testable in isolation.

const LIST_LIMIT = 25;
// Page through the whole set so the rail shows every conversation (129 over the
// reference range, 4,047 all-time) AND a drilled stage can intersect its
// conversation_ids against the FULL list, not just the first page. Capped so a
// large property cannot spin forever.
const MAX_LIST_PAGES = 40;

// The rail's filters are applied SERVER-SIDE via fn_get_conversation_list's own
// params (§A.5.9), so a narrowed rail fetches only the matching rows rather than
// filtering a partial page in memory. The intel-filter shape ('all' = unfiltered;
// yes/no for the booleans) maps onto the p_* params here. caller_type defaults to
// 'guest' via DEFAULT_INTEL_FILTERS — a visible control, not a hidden predicate.
const listParamsFromFilters = (filters, search) => {
  const f = filters || {};
  const params = {};
  if (f.outcome && f.outcome !== "all") params.p_outcome = f.outcome;
  if (f.stayType && f.stayType !== "all") params.p_stay_type = f.stayType;
  if (f.urgency && f.urgency !== "all") params.p_booking_urgency = f.urgency;
  if (f.callerType && f.callerType !== "all") params.p_caller_type = f.callerType;
  if (f.needsFollowup === "yes") params.p_requires_followup = true;
  else if (f.needsFollowup === "no") params.p_requires_followup = false;
  if (f.hasBarrier === "yes") params.p_has_barrier = true;
  else if (f.hasBarrier === "no") params.p_has_barrier = false;
  if (f.afterHours === "yes") params.p_after_hours = true;
  else if (f.afterHours === "no") params.p_after_hours = false;
  const q = (search || "").trim();
  if (q) params.p_search = q;
  return params;
};

// A RESERVATION row (Addendum 1 §4/§5.1). A reservation is not a conversation — it
// comes from agent_reservations and has no transcript to open — so it renders as a
// static info row, never a button that pushes a ConversationDetailPane. Its ONE
// affordance is the checkout link, and only when checkout_url is present: it is null
// on every unpaid row (a known backend gap), so a dead link is never rendered.
//
// The phone number is NOT a tel: link here, deliberately: §5.1 scopes the
// reservation-drill row to "the checkout_url link when present; otherwise a static
// info row", so a second affordance would over-reach — and this row also serves the
// PAID Reservations drill, which is not a call-back list. The "people to contact
// today" worklist that DOES make the phone tappable is InProgressPanel (§3).
const ReservationRow = ({ row }) => {
  const name = titleCase(row.guest_name) || "Guest";
  const checkout = row.checkout_url || null;
  const site = [row.site_name, row.site_number ? `site ${row.site_number}` : null].filter(Boolean).join(", ");
  const dates = row.check_in && row.check_out ? `${fmtDateLabel(row.check_in)} – ${fmtDateLabel(row.check_out)}` : null;
  const nights = row.nights != null ? `${row.nights} ${row.nights === 1 ? "night" : "nights"}` : null;
  const meta = [site || null, dates, nights].filter(Boolean).join(" · ");
  return (
    <div data-testid="drill-set-reservation" className="px-1 py-2 flex flex-col gap-1">
      <div className="flex items-start justify-between gap-2 min-w-0">
        {checkout ? (
          <a href={checkout} target="_blank" rel="noopener noreferrer" data-testid="reservation-checkout" className="gi-body font-medium min-w-0 break-words gi-link no-underline">
            {name}
          </a>
        ) : (
          <span className="gi-body font-medium gi-link min-w-0 break-words">{name}</span>
        )}
        {row.total != null && (
          <span className="gi-label shrink-0 text-gray-500 dark:text-gray-400 tabular-nums">{fmtCurrency(row.total)}</span>
        )}
      </div>
      {meta && <p className="gi-body text-gray-600 dark:text-gray-400 break-words">{meta}</p>}
    </div>
  );
};

// A minimal set body for the DrillOver. Branches on `row.kind`: a reservation row
// renders as static info (ReservationRow), a conversation row as a clickable list
// item that pushes the conversation level. (The richer PaginatedTable body lands
// with the detail work in §8.)
const DrillSet = ({ rows, onSelect, failed = false, onRetry }) => {
  if (failed) {
    return <PanelLoadFailed message="Couldn't load these conversations" onRetry={onRetry} />;
  }
  const list = Array.isArray(rows) ? rows : [];
  if (list.length === 0) {
    return <PanelEmpty message="Nothing in this set." hint="This drill matched no conversations." />;
  }
  return (
    <ul className="space-y-2">
      {list.map((row, i) => (
        <li key={row.reservation_id || row.conversation_id || row.id || i}>
          {row.kind === "reservation" ? (
            <ReservationRow row={row} />
          ) : (
            <button
              type="button"
              data-testid="drill-set-row"
              onClick={() => onSelect(row)}
              className={`group w-full text-left rounded-lg border border-gray-200 dark:border-gray-700/60 border-l-[3px] ${outcomeAccentClass(row.outcome)} bg-white dark:bg-gray-800/30 ${outcomeHoverClass(row.outcome)} shadow-sm px-3 py-2.5 hover:shadow-md transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]`}
            >
              <span className="flex items-center justify-between gap-2">
                {/* Caller identifier (with a channel icon) and date, both in the theme
                    link blue — gi-link, the accessible token. The "Unknown caller"
                    fallback is coloured too. */}
                <span className="flex items-center gap-1.5 min-w-0">
                  <svg className="w-3.5 h-3.5 shrink-0 text-gray-500 dark:text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    {row.modality === "chat" ? (
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    ) : (
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                    )}
                  </svg>
                  <span className="gi-body gi-link font-medium truncate">
                    {row.visitor_name || row.name || row.phone || "Unknown caller"}
                  </span>
                </span>
                {row.started_at && (
                  <span className="text-xs gi-link shrink-0">{fmtDateLabel(row.started_at)}</span>
                )}
              </span>
              {row.summary && (
                <span className="block gi-body text-gray-600 dark:text-gray-400 mt-1 break-words">{row.summary}</span>
              )}
            </button>
          )}
        </li>
      ))}
    </ul>
  );
};

// fn_drill returns {kind, rows[]} — the kind lives on the RESULT, not each row.
// Stamp it onto every row so DrillSet's per-row `row.kind` branch can tell a
// reservation set from a conversation set.
const tagRows = (res) => {
  const rows = Array.isArray(res?.rows) ? res.rows : [];
  return res?.kind ? rows.map((r) => ({ ...r, kind: res.kind })) : rows;
};

const ConversationPage = ({ clientId, range, modality = "voice", hourlyRate = null }) => {
  const start = range?.start;
  const end = range?.end;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Bumped by "Try again" on a panel whose load failed; re-runs the load batch.
  const [reloadKey, setReloadKey] = useState(0);
  const retryLoad = useCallback(() => setReloadKey((k) => k + 1), []);
  const [allRows, setAllRows] = useState([]);
  const [total, setTotal] = useState(null);
  const [listLoading, setListLoading] = useState(true);
  const [stack, setStack] = useState([]);
  const [railFilter, setRailFilter] = useState(null); // { label, ids }
  const [activeStage, setActiveStage] = useState(null);
  const [showAnalysisOnMobile, setShowAnalysisOnMobile] = useState(false);
  // The conversations rail is HIDDEN on first load (desktop) so the analysis reads
  // full-width and uncrowded; a toggle reveals it on demand. Mobile keeps its own
  // rail/analysis switch (showAnalysisOnMobile) and ignores this.
  const [railOpen, setRailOpen] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_INTEL_FILTERS);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("date");

  const scope = useMemo(
    () => ({ client_id: clientId, start_date: start, end_date: end, modality }),
    [clientId, start, end, modality],
  );

  // Load-time panel data — one Promise.all, modality threaded to every scoped RPC.
  useEffect(() => {
    if (!clientId || !start || !end) return undefined;
    let cancelled = false;
    setLoading(true);
    loadConversationData(clientId, { start, end, modality, hourlyRate }).then((d) => {
      if (!cancelled) { setData(d); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, [clientId, start, end, modality, hourlyRate, reloadKey]);

  // The rail's own list fetch (separate from the panel batch, §A.8). Pages through
  // the whole set so every row is reachable and a drill can filter across all of
  // them. Default p_caller_type:'guest' (§E.15).
  useEffect(() => {
    if (!clientId || !start || !end) return undefined;
    let cancelled = false;
    setListLoading(true);
    const filterParams = listParamsFromFilters(filters, search);
    (async () => {
      const rows = [];
      let reported = null;
      try {
        for (let page = 1; page <= MAX_LIST_PAGES; page += 1) {
          // eslint-disable-next-line no-await-in-loop
          const res = await getConversationList(clientId, {
            page, limit: LIST_LIMIT,
            p_modality: modality, p_start_date: start, p_end_date: end,
            ...filterParams,
          });
          const items = Array.isArray(res?.items) ? res.items : [];
          rows.push(...items);
          reported = res?.pagination?.total ?? reported;
          const totalPages = Number(res?.pagination?.total_pages) || 1;
          if (items.length === 0 || page >= totalPages) break;
        }
        if (!cancelled) { setAllRows(rows); setTotal(reported ?? rows.length); setListLoading(false); }
      } catch {
        if (!cancelled) { setAllRows(rows); setListLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [clientId, start, end, modality, filters, search]);

  // Clearing the rail filters resets to the defaults (caller_type back to 'guest')
  // and drops any drill-narrow — the "Clear" affordance §E.15 preserves.
  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_INTEL_FILTERS);
    setSearch("");
  }, []);

  // CSV export (§E.15). The tested api helper selects the whole conversations
  // table (base columns); the intel columns are joined from an UNFILTERED
  // full-population intel map (getConversationIntelMap sends no caller-type
  // filter), NOT the guest-scoped allRows — so every conversation, not just
  // guests, exports its outcome/stay-type/urgency/barriers/value, matching the
  // v1 export which enriched the full population.
  const onExportCsv = useCallback(async () => {
    const intelMap = await getConversationIntelMap(clientId, { modality, startDate: start, endDate: end });
    return downloadTranscriptsCsv(clientId, start, end, modality, intelExtraColumns(Object.values(intelMap)));
  }, [clientId, start, end, modality]);

  const closeDrill = useCallback(() => setStack([]), []);
  const popDrill = useCallback(() => setStack((s) => (s.length > 0 ? s.slice(0, -1) : s)), []);
  const pushDrill = useCallback((level) => { if (level) setStack((s) => [...s, level]); }, []);

  // The guest level — cdp/PersonProfile pushed onto the stack (§E.16 block 8, §8).
  // Reused, never a second profile layout, and never filled locally.
  const openGuest = useCallback((personId, label) => {
    if (!personId) return;
    pushDrill({
      key: `guest-${personId}`,
      title: label || "Guest",
      body: <PersonProfile clientId={clientId} personId={personId} />,
    });
  }, [pushDrill, clientId]);

  // Open one conversation as a stacked level — the real ConversationDetailPane
  // (§E.16 block order), which fires fn_get_conversation_detail +
  // fn_get_conversation_intelligence together on mount. Its guest card pushes the
  // PersonProfile level via onOpenGuest rather than linking out to /visitors.
  const openConversation = useCallback((row) => {
    if (!row) return;
    const id = row.conversation_id || row.id;
    pushDrill({
      key: `conv-${id}`,
      // `title` still labels the dialog for screen readers; `titleNode` is the rich
      // identity header shown in place of the plain title/subtitle.
      title: row.visitor_name || row.name || row.phone || "Conversation",
      titleNode: (
        <ConversationHeader
          convo={row}
          modality={modality}
          fallbackName={row.visitor_name || row.name || row.phone}
        />
      ),
      body: (
        <ConversationDetailPane
          conversationId={id}
          clientId={clientId}
          convo={row}
          modality={modality}
          hideHeader
          getMsgSide={getMsgSide}
          getMsgText={getMsgText}
          getMsgTime={getMsgTime}
          formatTime={formatTimeOnlySafe}
          onOpenGuest={openGuest}
        />
      ),
    });
  }, [pushDrill, clientId, modality, openGuest]);

  // onSelect is KIND-AWARE by construction: DrillSet invokes it only for
  // conversation rows. A reservation row (row.kind === "reservation") has no
  // transcript, so DrillSet renders it as static info and never calls onSelect —
  // openConversation is unreachable for reservations.
  const openSet = useCallback((title, rows, { failed = false, onRetry } = {}) => {
    pushDrill({
      key: `set-${title}`,
      title,
      body: <DrillSet rows={rows} onSelect={openConversation} failed={failed} onRetry={onRetry} />,
    });
  }, [pushDrill, openConversation]);

  /**
   * Run one fn_drill and open its set. A failed request (e.g. a statement timeout
   * on a long range) opens the set in its "Couldn't load" state rather than as an
   * empty set; "Try again" replaces that level with a fresh attempt.
   * @param {string} title the set's title
   * @param {() => Promise<object>} request the fn_drill call
   * @param {(res: object) => object[]} toRows maps the result to rows
   */
  const runDrill = useCallback((title, request, toRows) => {
    const attempt = () => request()
      .then((res) => openSet(title, toRows(res)))
      .catch(() => openSet(title, [], {
        failed: true,
        onRetry: () => { popDrill(); attempt(); },
      }));
    attempt();
  }, [openSet, popDrill]);

  // Expand a full-population id list to rows. `allRows` is the guest-scoped rail
  // list, but a panel's conversation_ids are full-population (the drain/funnel
  // count all caller types), so intersecting-and-dropping would open a set
  // smaller than the count the user just clicked (§F.4). Map EVERY id: enriched
  // from the loaded set where present, a bare {conversation_id} otherwise —
  // never drop one. Stages carry their ids inline (no fn_drill kind), which is
  // why this is a local expansion rather than a backend re-fetch.
  const byId = useMemo(() => new Map(allRows.map((r) => [r.conversation_id, r])), [allRows]);
  const rowsForIds = useCallback(
    (ids) => (Array.isArray(ids) ? ids : []).map((id) => byId.get(id) || { conversation_id: id }),
    [byId],
  );

  // A finding drills either a known set of ids or via fn_drill.
  const onFinding = useCallback((descriptor) => {
    if (!descriptor) return;
    if (descriptor.kind === "ids") {
      openSet(descriptor.title || "Conversations", rowsForIds(descriptor.ids));
      return;
    }
    runDrill(
      descriptor.title || "Conversations",
      () => fnDrill(descriptor.what, descriptor.id ?? null, scope),
      (res) => res?.rows || [],
    );
  }, [rowsForIds, openSet, runDrill, scope]);

  const onBarrierDrill = useCallback((drillObj) => {
    if (!drillObj) return;
    runDrill(
      drillObj.id || "Barrier",
      () => fnDrill(drillObj.what || "barrier", drillObj.id ?? null, scope),
      (res) => res?.rows || [],
    );
  }, [runDrill, scope]);

  // Open a set from a known list of conversation_ids — always the FULL list
  // (rowsForIds enriches where loaded, bare otherwise), never the loaded subset.
  const openIdsSet = useCallback((title, ids) => {
    openSet(title, rowsForIds(ids));
  }, [rowsForIds, openSet]);

  // Open a set via fn_drill for a (what, id) the payload gave us. Rows are tagged
  // with the result kind so a 'reservation' drill (paid/unpaid) renders reservation
  // rows rather than opening a conversation that does not exist.
  const openDrillSet = useCallback((what, id, title) => {
    runDrill(title, () => fnDrill(what, id ?? null, scope), tagRows);
  }, [runDrill, scope]);

  const onDemandSelect = useCallback(
    (sel) => openDrillSet("stay_type", sel?.key || sel?.label, sel?.label || "Demand"),
    [openDrillSet],
  );
  const onFollowupSelect = useCallback((it) => openConversation(it), [openConversation]);
  // Title-case the gap topic for the slide-over title so it reads "Overflow Parking",
  // not the raw lowercase "overflow parking" — matching how WebsiteGapsPanel renders it.
  const onGapSelect = useCallback(
    (gap) => openIdsSet(gap?.topic ? titleCase(gap.topic) : "Guest question", gap?.example_conversations || []),
    [openIdsSet],
  );
  // The gap panel's secondary rail-narrow; not wired to a rail filter in this pass.
  const onGapFilter = useCallback(() => {}, []);
  // ChatQuestions topics are snake_case tokens ("dump_station"), so labelFor — not
  // titleCase — turns them into a readable title, the same as ChatQuestionsPanel.
  const onTopicSelect = useCallback(
    (topic) => openIdsSet(topic?.topic ? labelFor(topic.topic) : "Topic", topic?.conversation_ids || []),
    [openIdsSet],
  );
  // Workload figures drill with the descriptor the backend ships beside each count;
  // the title is the figure's own (modality-aware) label.
  const onWorkloadDrill = useCallback(
    (drillObj, title) => { if (drillObj?.what) openDrillSet(drillObj.what, drillObj.id ?? null, title); },
    [openDrillSet],
  );
  // The money strip's drill sources (Addendum 1 §2): Reservations
  // (reservation/paid), Booked after talking (outcome/reservation_made), In progress
  // (reservation/unpaid), and the fourth cell — After hours (after_hours/null) on
  // Phone or Booking enquiries (outcome/reservation_assisted) on Chat. Each cell
  // supplies its own {what, id}.
  const onMoneyDrill = useCallback(
    (drillObj, title) => { if (drillObj) openDrillSet(drillObj.what, drillObj.id ?? null, title || "Conversations"); },
    [openDrillSet],
  );
  const onAttributionSelect = useCallback((row) => {
    const d = row?.drill;
    openDrillSet(d?.what || "source", d?.id ?? row?.source, row?.source || "Source");
  }, [openDrillSet]);

  // A stage does three things at once: highlight, filter the rail, open the drill.
  const onStageClick = useCallback((stage) => {
    if (!stage) return;
    const ids = Array.isArray(stage.conversation_ids) ? stage.conversation_ids : [];
    setActiveStage(stage.key);
    setRailFilter({ label: stage.label || stage.key, ids });
    openSet(stage.label || stage.key, rowsForIds(ids));
  }, [rowsForIds, openSet]);

  // Dismissing the chip clears all three legs.
  const clearRailFilter = useCallback(() => {
    setRailFilter(null);
    setActiveStage(null);
  }, []);

  const say = useMemo(() => data?.sentences?.sentences || {}, [data]);
  // A drill narrows the rail to the stage's FULL conversation_ids (§F.4), not the
  // guest-loaded subset — so the rail count matches the panel count the user clicked.
  const railRows = railFilter ? rowsForIds(railFilter.ids) : allRows;
  const sortedRailRows = sortRailRows(railRows, sortBy);

  // Memoised so its identity is stable across unrelated re-renders (e.g. opening the
  // drill drawer), which lets the memoised AnalysisColumn skip re-rendering its charts.
  const analysisHeaderAction = useMemo(() => (
    <button
      type="button"
      onClick={() => setRailOpen((v) => !v)}
      aria-pressed={railOpen}
      data-testid="rail-toggle"
      className={`hidden md:inline-flex shrink-0 items-center gap-2 min-h-[36px] px-3 rounded-md text-sm font-medium border transition-colors ${
        railOpen
          ? "bg-[#32B1FF] border-[#32B1FF] text-white hover:bg-[#2175A8] hover:border-[#2175A8]"
          : "text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-800 hover:bg-gray-100 dark:hover:bg-gray-800"
      }`}
    >
      {/* Icon reflects the channel: a chat bubble on Chat, a phone on Phone. */}
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {modality === "chat" ? (
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        ) : (
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
        )}
      </svg>
      {railOpen ? "Hide conversations" : "Show conversations"}
    </button>
  ), [railOpen, modality]);

  return (
    <div data-testid="conversation-page" className="flex-1 min-h-0 h-full">
      {/* Mobile: rail is the default view; analysis is behind a toggle (below md). */}
      <div className="md:hidden flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-800">
        <span className="gi-label text-gray-500 dark:text-gray-400">
          {modality === "chat" ? "Chat" : "Phone"}
        </span>
        <button
          type="button"
          onClick={() => setShowAnalysisOnMobile((v) => !v)}
          className="md:hidden shrink-0 min-h-[44px] px-3 rounded-md text-sm font-medium gi-link"
        >
          {showAnalysisOnMobile ? "Show conversations" : "Show analysis"}
        </button>
      </div>

      {/* SIDE-BY-SIDE — on md+ the conversations rail is a 340px LEFT column shown
          only when toggled; hidden, the analysis takes the full width (one column).
          A "Show conversations" toggle sits inline with the analysis title and swaps
          it to "Hide conversations". Mobile keeps its own rail/analysis switch
          (above). min-h-0 on both columns so each scrolls independently; the rail
          stays mounted so its list survives the toggle. */}
      <div className={`grid h-full min-h-0 ${railOpen ? "md:grid-cols-[340px_1fr]" : "md:grid-cols-1"}`}>
        {/* Rail — LEFT column. Mobile: the default view (unless analysis is toggled).
            md+: shown only when railOpen. */}
        <div
          className={`min-h-0 h-full border-r border-gray-200 dark:border-gray-800 ${
            showAnalysisOnMobile ? "hidden" : "block"
          } ${railOpen ? "md:block" : "md:hidden"}`}
        >
          <ConversationRail
            rows={sortedRailRows}
            total={railFilter ? railRows.length : total}
            loading={listLoading}
            modality={modality}
            activeFilter={railFilter}
            onClearFilter={clearRailFilter}
            onRowClick={openConversation}
            filters={filters}
            onFiltersChange={setFilters}
            onClearFilters={clearFilters}
            search={search}
            onSearchChange={setSearch}
            sortBy={sortBy}
            onSortChange={setSortBy}
            onExportCsv={onExportCsv}
          />
        </div>

        {/* Analysis — RIGHT column; independent scroll (min-h-0 lets it scroll).
            Mobile: behind the "Show analysis" toggle. md+: always visible, sitting
            beside the rail when it is open. */}
        <div
          className={`min-h-0 h-full overflow-y-auto px-4 md:px-6 lg:px-8 py-6 ${
            showAnalysisOnMobile ? "block" : "hidden md:block"
          }`}
        >
          <AnalysisColumn
            headerAction={analysisHeaderAction}
            onRetry={retryLoad}
            modality={modality}
            data={data}
            say={say}
            clientId={clientId}
            range={range}
            loading={loading}
            activeStage={activeStage}
            onFinding={onFinding}
            onStageClick={onStageClick}
            onBarrierDrill={onBarrierDrill}
            onDemandSelect={onDemandSelect}
            onFollowupSelect={onFollowupSelect}
            onGapSelect={onGapSelect}
            onGapFilter={onGapFilter}
            onTopicSelect={onTopicSelect}
            onWorkloadDrill={onWorkloadDrill}
            onMoneyDrill={onMoneyDrill}
            onAttributionSelect={onAttributionSelect}
          />
        </div>
      </div>

      {/* The one drill destination. */}
      <DrillOver stack={stack} onBack={popDrill} onClose={closeDrill} />
    </div>
  );
};

export default ConversationPage;
